import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authService } from '../services/authService'
import { registerLogoutHandler, unregisterLogoutHandler } from '../utils/authHelpers'
import {
  clearPushSessionStorage,
  syncPushForAuthenticatedUser,
  unregisterPushForSession
} from '../services/pushSessionService'

const AuthContext = createContext(null)

function withTimeout(promise, timeoutMs = 1500) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(resolve, timeoutMs))
  ])
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          try {
            const userData = await authService.getMe()
            setUser(userData)
          } catch (error) {
            // Si hay un error al obtener el usuario pero tenemos token,
            // verificar si es un error temporal (429, 500, etc.) o de autenticación (401)
            const status = error.response?.status
            const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message
            
            // Si es un error 429 (Too Many Requests) o 500 (Error del servidor),
            // mantener el usuario logueado y solo mostrar el error
            if (status === 429 || status === 500 || status === 503) {
              console.warn('⚠️ Error temporal al obtener usuario, manteniendo sesión:', {
                status,
                message: errorMessage
              })
              // Intentar usar el usuario guardado en localStorage como fallback
              const savedUser = authService.getUser()
              if (savedUser) {
                setUser(savedUser)
              }
            } else if (status === 401) {
              // Solo hacer logout si es un error 401 (token inválido)
              console.error('❌ Token inválido al inicializar:', errorMessage)
              clearPushSessionStorage()
              authService.logout()
            } else {
              // Otros errores: mantener sesión pero sin usuario
              console.warn('⚠️ Error al obtener usuario:', {
                status,
                message: errorMessage
              })
              const savedUser = authService.getUser()
              if (savedUser) {
                setUser(savedUser)
              }
            }
          }
        }
      } catch (error) {
        console.error('Error crítico al inicializar autenticación:', error)
        // Solo hacer logout si es un error crítico
        if (error.response?.status === 401) {
          clearPushSessionStorage()
          authService.logout()
        }
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (email, password) => {
    const { user } = await authService.login(email, password)
    setUser(user)
    return user
  }

  const register = async (userData) => {
    const { user } = await authService.register(userData)
    setUser(user)
    return user
  }

  const loginWithPhone = async (telefono, otpCode) => {
    const { user } = await authService.loginWithPhone(telefono, otpCode)
    setUser(user)
    return user
  }

  const registerWithPhone = async (userData) => {
    const { user } = await authService.registerWithPhone(userData)
    setUser(user)
    return user
  }

  const socialLogin = async (provider, providerId, email, nombre, apellido, foto) => {
    const { user } = await authService.socialLogin(provider, providerId, email, nombre, apellido, foto)
    setUser(user)
    return user
  }

  const logout = useCallback(async () => {
    try {
      // Evitar que el token quede asociado al usuario anterior en el mismo dispositivo.
      await withTimeout(unregisterPushForSession())
    } catch (error) {
      console.warn('No se pudo completar cleanup de push en logout:', error?.message || error)
    }

    authService.logout()
    setUser(null)
  }, [])

  useEffect(() => {
    if (!user || !authService.isAuthenticated()) return

    syncPushForAuthenticatedUser(user).catch((error) => {
      console.warn('No se pudo sincronizar token push para la sesión actual:', error?.message || error)
    })
  }, [user])

  // Registrar la función de logout globalmente cuando el componente se monta
  useEffect(() => {
    registerLogoutHandler(() => {
      logout()
    })

    // Limpiar cuando el componente se desmonta
    return () => {
      unregisterLogoutHandler()
    }
  }, [logout])

  const refreshUser = async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = await authService.getMe()
        
        // Normalizar avatar/foto si existe
        if (userData) {
          const { getAvatarUrl } = await import('../utils/avatarHelpers')
          const normalizedAvatarUrl = getAvatarUrl(userData)
          if (normalizedAvatarUrl) {
            userData.avatar = normalizedAvatarUrl
            userData.foto = normalizedAvatarUrl
          }
        }
        
        setUser(userData)
        // Actualizar localStorage también
        if (userData) {
          localStorage.setItem('user', JSON.stringify(userData))
        }
        
        return userData
      }
    } catch (error) {
      // Si es un error 401, el token expiró, hacer logout
      if (error.response?.status === 401) {
        console.error('❌ Token expirado al refrescar usuario, deslogueando...')
        logout()
      } else {
        console.error('Error al refrescar usuario:', error)
      }
    }
    return null
  }

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    loginWithPhone,
    registerWithPhone,
    socialLogin,
    logout,
    refreshUser,
    isAuthenticated: !!user && authService.isAuthenticated(),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider')
  }
  return context
}

